import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../contexts/AuthContext";
import { ETHIOPIAN_REGIONAL_CAPITALS } from "../../../constants/ethiopia";

const USER_API = process.env.REACT_APP_USER_SERVICE_URL;
const API_BASE = USER_API + "/sellers";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["application/pdf"];
const Required = () => <span className="text-danger ms-1">*</span>;

export default function SellerRequestPage() {
  const { auth } = useAuth();

  // Form state
  const [companyName, setCompanyName] = useState("");
  const [businessLicense, setBusinessLicense] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [address, setAddress] = useState({
    addressType: "BILLING",
    country: "Ethiopia",
    city: "",
    subCity: "",
    street: "",
    houseNumber: "",
    postalCode: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Seller request status: NONE | PENDING | APPROVED | REJECTED
  const [sellerStatus, setSellerStatus] = useState("NONE");

  /* ---------------- FETCH CURRENT USER STATUS ---------------- */
  useEffect(() => {
    const fetchUserStatus = async () => {
      try {
        const res = await axios.get(`${USER_API}/users/${auth.userId}`, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        const updatedUser = res.data;
        setSellerStatus(updatedUser.sellerStatus || "NONE");
      } catch (err) {
        console.error("Failed to fetch user status", err);
      }
    };

    fetchUserStatus();
  }, [auth.userId, auth.token]);

  /* ---------------- FILE HANDLING ---------------- */
  const validateFile = (file) => {
    if (!file) return "Business license is required";
    if (!ALLOWED_TYPES.includes(file.type)) return "Only PDF files are allowed";
    if (file.size > MAX_FILE_SIZE) return "File size must be less than 5 MB";
    return null;
  };

  useEffect(() => {
    if (!businessLicense) {
      setFilePreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(businessLicense);
    setFilePreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [businessLicense]);

  /* ---------------- FORM VALIDATION ---------------- */
  const validateForm = () => {
    const newErrors = {};

    if (!companyName.trim()) newErrors.companyName = "Company name is required";
    if (!address.city) newErrors.city = "City is required";

    const fileError = validateFile(businessLicense);
    if (fileError) newErrors.businessLicense = fileError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = useMemo(
    () =>
      companyName.trim() &&
      address.city &&
      businessLicense &&
      !validateFile(businessLicense) &&
      !loading,
    [companyName, address.city, businessLicense, loading],
  );

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess(false);

    if (!validateForm()) return;

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("companyName", companyName);
      formData.append("businessLicense", businessLicense);
      formData.append("businessAddress", JSON.stringify(address));

      await axios.post(`${API_BASE}/${auth.userId}/upgrade`, formData, {
        headers: { Authorization: `Bearer ${auth.token}` },
      });

      setSuccess(true);
      setSellerStatus("PENDING"); // immediately reflect pending status
    } catch (err) {
      console.error(err);
      setErrors({
        submit:
          err.response?.data?.message || "Failed to submit seller request",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- CONDITIONAL RENDERING BASED ON STATUS ---------------- */
  if (sellerStatus === "PENDING") {
    return (
      <div className="alert alert-info mt-3">
        Your seller request is pending approval. Please wait for admin review.
      </div>
    );
  }

  if (sellerStatus === "APPROVED") {
    return (
      <div className="alert alert-success mt-3">
        Congratulations! Your seller account has been approved.
        <a href="https://seller.afrogebeya.com"> Go to Seller Central</a>
      </div>
    );
  }

  if (sellerStatus === "REJECTED") {
    return (
      <div className="alert alert-danger mt-3">
        Your seller request was rejected. You may submit a new request.
      </div>
    );
  }

  /* ---------------- RENDER FORM IF STATUS NONE ---------------- */
  return (
    <div>
      <h3 className="fw-bold mb-3">Become a Seller</h3>

      <form className="card p-4 shadow-sm" onSubmit={handleSubmit} noValidate>
        {/* COMPANY NAME */}
        <div className="mb-3">
          <label className="form-label">
            Company Name <Required />
          </label>
          <input
            className={`form-control ${errors.companyName ? "is-invalid" : ""}`}
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
          {errors.companyName && (
            <div className="invalid-feedback">{errors.companyName}</div>
          )}
        </div>

        {/* BUSINESS LICENSE */}
        <div className="mb-3">
          <label className="form-label">
            Business License (PDF only) <Required />
          </label>
          <input
            type="file"
            className={`form-control ${
              errors.businessLicense ? "is-invalid" : ""
            }`}
            accept="application/pdf"
            onClick={(e) => (e.target.value = null)}
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setBusinessLicense(file);
              setErrors((prev) => ({
                ...prev,
                businessLicense: validateFile(file),
              }));
            }}
          />
          {errors.businessLicense && (
            <div className="invalid-feedback">{errors.businessLicense}</div>
          )}
        </div>

        {/* PDF PREVIEW */}
        {filePreviewUrl && (
          <div className="mb-3">
            <label className="form-label fw-semibold">
              Business License Preview
            </label>
            <iframe
              src={filePreviewUrl}
              title="Business License Preview"
              className="w-100 border rounded"
              style={{ height: "400px" }}
            />
          </div>
        )}

        {/* ADDRESS */}
        <h6 className="fw-semibold mt-4">Business Address</h6>

        <div className="mb-2">
          <label className="form-label">Country</label>
          <input className="form-control" value="Ethiopia" disabled />
        </div>

        <div className="mb-2">
          <label className="form-label">
            City (Regional Capital) <Required />
          </label>
          <select
            className={`form-select ${errors.city ? "is-invalid" : ""}`}
            value={address.city}
            onChange={(e) => setAddress({ ...address, city: e.target.value })}
          >
            <option value="">-- Select City --</option>
            {ETHIOPIAN_REGIONAL_CAPITALS.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
          {errors.city && <div className="invalid-feedback">{errors.city}</div>}
        </div>

        {["subCity", "street", "houseNumber", "postalCode"].map((f) => (
          <div className="mb-2" key={f}>
            <label className="form-label">{f.replace(/([A-Z])/g, " $1")}</label>
            <input
              className="form-control"
              value={address[f]}
              onChange={(e) => setAddress({ ...address, [f]: e.target.value })}
            />
          </div>
        ))}

        {errors.submit && (
          <div className="alert alert-danger mt-3">{errors.submit}</div>
        )}

        {success && (
          <div className="alert alert-success mt-3">
            Seller request submitted successfully. Await approval.
          </div>
        )}

        <button
          className="btn btn-warning mt-3"
          disabled={!isFormValid || loading}
        >
          {loading ? "Submitting..." : "Submit Seller Request"}
        </button>
      </form>
    </div>
  );
}
